// src/contexts/ReviewContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';

const ReviewContext = createContext();

export const ReviewProvider = ({ children }) => {
  const [reviews, setReviews] = useState({}); // { bookId: [reviews] }

  // Load reviews for a book
  const loadReviews = async (bookId) => {
    try {
      const q = query(collection(db, 'books', bookId, 'reviews'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const reviewList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setReviews(prev => ({ ...prev, [bookId]: reviewList }));
      return reviewList;
    } catch (error) {
      console.error("Error loading reviews:", error);
      return [];
    }
  };

  // Add a new review
  const addReview = async (bookId, reviewData) => {
    try {
      const docRef = await addDoc(collection(db, 'books', bookId, 'reviews'), {
        ...reviewData,
        createdAt: serverTimestamp()
      });

      const newReview = {
        id: docRef.id,
        ...reviewData,
        createdAt: new Date()
      };

      setReviews(prev => ({
        ...prev,
        [bookId]: [newReview, ...(prev[bookId] || [])]
      }));

      return newReview;
    } catch (error) {
      console.error("Error adding review:", error);
      throw error;
    }
  };

  // Get reviews for a book
  const getReviews = (bookId) => reviews[bookId] || [];

  return (
    <ReviewContext.Provider value={{ reviews, loadReviews, addReview, getReviews }}>
      {children}
    </ReviewContext.Provider>
  );
};

export const useReviews = () => {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error('useReviews must be used within ReviewProvider');
  }
  return context;
};